import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Send } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.status === "subscribed") {
        setStatus("success");
        setEmail("");
      } else if (data.status === "already_subscribed") {
        setStatus("already");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  const quickLinks = [
    { name: "About Cooper", path: "/about" },
    { name: "Events", path: "/events" },
    { name: "News", path: "/news" },
    { name: "Contact", path: "/contact" },
    { name: "Volunteer", path: "/volunteer" },
    { name: "I Need You", path: "/spread-the-word" },
  ];

  const policies = [
    { name: "Lowering Housing Costs", path: "/policies/housing" },
    { name: "Lowering Rental Costs", path: "/policies/rental" },
    { name: "Clean Energy & Jobs", path: "/policies/clean-energy" },
    { name: "Traffic & Non-Violent (TNV)", path: "/policies/tnv" },
    { name: "Ending Food Deserts and Food Swamps", path: "/policies/food-deserts" },
    { name: "No NV Taxpayer Funded AI Data Centers", path: "/policies/no-ai-subsidies" },
    { name: "Protect Jobs From AI Automation", path: "/policies/automation" },
    { name: "eNVy Healthcare", path: "/policies/healthcare" },
    { name: "Pretrial Justice Reform", path: "/policies/bail-reform" },
    { name: "Post-Justice Reform", path: "/policies/post-justice" },
    { name: "NDA Accountability", path: "/policies/nda-reform" },
    { name: "Protect Public Schools", path: "/policies/education" },
    { name: "Federal Funding & Infrastructure", path: "/policies/federal-funding" },
    { name: "Pro-Union", path: "/policies/pro-union" },
    { name: "Pro-Worker, NOT Anti-Business", path: "/policies/pro-worker" },
    { name: "NV Health Insurance Reform", path: "/policies/health-insurance" },
    { name: "Ending Unfair Arbitration", path: "/policies/arbitration" },
    { name: "More Coming Soon", path: "/policies/more-coming-soon" },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0B1A3B] text-white">
      {/* Newsletter Bar */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-white">Stay Updated</h3>
              <p className="text-white/60 text-sm">
                Get campaign news, policy updates, and event announcements.
              </p>
            </div>
            <form
              onSubmit={handleNewsletter}
              data-testid="newsletter-form"
              className="flex w-full md:w-auto gap-2"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="newsletter-email-input"
                className="px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm w-full md:w-72 focus:outline-none focus:border-[#D4AF37]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="px-6 py-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
          {status === "success" && (
            <p className="text-emerald-400 text-sm mt-3 text-center md:text-right">
              You're in! Check your email for a welcome message.
            </p>
          )}
          {status === "already" && (
            <p className="text-[#D4AF37] text-sm mt-3 text-center md:text-right">
              You're already subscribed!
            </p>
          )}
          {status === "error" && (
            <p className="text-red-400 text-sm mt-3 text-center md:text-right">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <span className="text-3xl font-black tracking-tight">COOPER</span>
              <span className="block text-sm font-bold text-[#D4AF37] tracking-widest mt-1">
                FOR NEVADA
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              For You. For Your Family. For Nevada.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com/CooperForNV"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-[#D4AF37] hover:text-black transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://tiktok.com/@cooperfornv"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-[#D4AF37] hover:text-black transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/share/1KjygS1tMb/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-[#D4AF37] hover:text-black transition-all"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-6">
              Key Solutions
            </h4>
            <ul className="space-y-3">
              {policies.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-6">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <a href="mailto:Info@CooperForNV.com" className="hover:text-white transition-colors">
                  Info@CooperForNV.com
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <Link
                to="/volunteer"
                data-testid="footer-volunteer-btn"
                className="inline-block px-6 py-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                Volunteer
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-xs text-center md:text-left">
              Paid for by Cooper for Nevada. Contributions are not tax-deductible.
            </p>
            <div className="flex items-center gap-6">
              <div id="google_translate_element" data-testid="translate-widget" />
              <Link to="/privacy" className="text-xs text-white/50 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-xs text-white/50 hover:text-white transition-colors">
                Terms of Use
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
